/**
 * Utility Functions - Shopify Theme Collet Brand
 *
 * Centralized utility functions used across the theme.
 * Eliminates code duplication and provides single source of truth.
 *
 * Functions exported via window object for backward compatibility.
 */

/**
 * Debounce function calls
 * Delays function execution until after wait time has elapsed since last call
 *
 * @param {Function} fn - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function} Debounced function
 *
 * @example
 * const debouncedSearch = debounce(performSearch, 300);
 * searchInput.addEventListener('input', debouncedSearch);
 */
function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Generate fetch configuration object for Shopify API requests
 *
 * @param {string} type - Response type ('json' or 'javascript')
 * @param {string} method - HTTP method (default: 'POST')
 * @returns {Object} Fetch configuration object
 *
 * @example
 * const config = fetchConfig();
 * fetch('/cart/add.js', { ...config, body: JSON.stringify(data) });
 */
function fetchConfig(type = 'json', method = 'POST') {
  return {
    method: method,
    headers: {
      'Content-Type': 'application/json',
      'Accept': `application/${type}`
    }
  };
}

/**
 * Pause all media on the page (videos, YouTube, Vimeo, 3D models)
 * Used when changing product variants or closing modals
 *
 * @example
 * pauseAllMedia(); // Pauses all media elements
 */
function pauseAllMedia() {
  // Pause YouTube videos
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + 'pauseVideo' + '","args":""}',
      '*'
    );
  });

  // Pause Vimeo videos
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });

  // Pause HTML5 videos
  document.querySelectorAll('video').forEach((video) => video.pause());

  // Pause 3D product models
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

/**
 * Get all focusable elements within a container
 * Used for accessibility (keyboard navigation, focus trapping)
 *
 * @param {HTMLElement} container - Container element to search within
 * @returns {Array<HTMLElement>} Array of focusable elements
 *
 * @example
 * const focusable = getFocusableElements(modalElement);
 * focusable[0].focus(); // Focus first element
 */
function getFocusableElements(container) {
  if (!container || !container.querySelectorAll) {
    console.warn('[getFocusableElements] Invalid container:', container);
    return [];
  }

  return Array.from(
    container.querySelectorAll(
      'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe'
    )
  );
}

/**
 * Trap focus within a container (for modals, drawers, etc.)
 * Prevents tabbing outside the container for accessibility
 *
 * @param {HTMLElement} container - Container to trap focus within
 * @param {HTMLElement} elementToFocus - Element to focus initially (defaults to container)
 *
 * @example
 * trapFocus(modalElement); // Trap focus in modal
 */
const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  const elements = getFocusableElements(container);
  const first = elements[0];
  const last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return;

    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    // On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();
}

/**
 * Remove focus trap from container
 * Cleanup function for trapFocus()
 *
 * @param {HTMLElement} elementToFocus - Element to focus after removing trap (optional)
 *
 * @example
 * removeTrapFocus(triggerButton); // Remove trap and focus trigger
 */
function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

/**
 * Serialize form data to JSON string
 * Useful for AJAX form submissions
 *
 * @param {HTMLFormElement} form - Form element to serialize
 * @returns {string} JSON string of form data
 *
 * @example
 * const json = serializeForm(formElement);
 * fetch('/endpoint', { body: json });
 */
function serializeForm(form) {
  const obj = {};
  const formData = new FormData(form);
  for (const key of formData.keys()) {
    obj[key] = formData.get(key);
  }
  return JSON.stringify(obj);
}

// Export functions to window for backward compatibility
if (typeof window !== 'undefined') {
  window.debounce = debounce;
  window.fetchConfig = fetchConfig;
  window.pauseAllMedia = pauseAllMedia;
  window.getFocusableElements = getFocusableElements;
  window.trapFocus = trapFocus;
  window.removeTrapFocus = removeTrapFocus;
  window.serializeForm = serializeForm;
}
